/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Button } from '@ziynx/components/buttons';
import BannerNotification from '@ziynx/components/notifications';
import Link from '@ziynx/components/links';
import Input from '@ziynx/components/inputs';
import { Form, FormRow } from '@ziynx/components/forms';
import List from '@ziynx/components/lists';
import {
  withAppManager, getMeasures, nameInputsRegex, emailRegex,
} from '@ziynx/components/utilities';
import Dropdown from '@ziynx/components/selects';
import Colors from '@ziynx/theme/colors';
import { useParams, useHistory } from 'react-router-dom';
import { ContentContainer } from '@ziynx/components/layout';
import ActionTypes from '@ziynx/action-types';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import {
  ownerPermissions, adminPermissions, managerPermissions, ROLES,
} from './teamConstants';

type History = {
  push: (pathName: string) => void,
  goBack: () => void
};

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: {
    goBack: () => void,
  },
|}>;

const PAGE_WIDTH = {
  mobileSmall: '100%',
  default: '688px',
};

const PAGE_PADDING = {
  mobileSmall: '16px',
  mobile: '16px',
  default: '48px',
};

const PAGE_PADDING_TOP = {
  mobileSmall: '48px',
  mobile: '48px',
  default: '40px',
};

const LINK_PADDING = {
  mobileSmall: '32px',
  mobile: '32px',
  tablet: '32px',
  default: '48px',
};

const StyledBannerWrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 996;
  width: 100%;
  flex: 1;
`;

const PageContentWrapper = styled.div`
  padding-left: ${({ viewport }) => getMeasures(PAGE_PADDING, viewport)};
  padding-right: ${({ viewport }) => getMeasures(PAGE_PADDING, viewport)};
  padding-top: ${({ viewport }) => getMeasures(PAGE_PADDING_TOP, viewport)};
  padding-bottom: 64px;
  max-width: ${({ viewport }) => getMeasures(PAGE_WIDTH, viewport)};
  width: 100%;
  box-sizing: border-box;
  ${({ isMobile }) => isMobile && 'margin: auto;'}
`;

const LinkWrapper = styled.div`
  padding-bottom: ${({ viewport }) => getMeasures(LINK_PADDING, viewport)};
`;

const ListWrapper = styled.div`
  padding-bottom: ${({ viewport }) => (viewport === 'tablet' ? '58px' : '40px')};
`;

const _getPermissions = (currentRole) => {
  switch (currentRole) {
  case 'owner':
    return ownerPermissions;
  case 'admin':
    return adminPermissions;
  case 'manager':
  default:
    return managerPermissions;
  }
};

const INPUT_FIELDS = [
  'firstName',
  'lastName',
  'role',
];

const EditTeamMemberScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';
  const dispatch = useDispatch();
  const history: History = useHistory();
  const {
    // eslint-disable-next-line max-len
    control, errors, clearErrors, handleSubmit, reset, register, formState: { dirtyFields },
  } = useForm();

  const { memberId } = useParams();

  const teamMemberService = useSelector(({ teamMember }) => teamMember[memberId] || {},
    shallowEqual);

  const [currentRole, setCurrentRole] = useState(teamMemberService?.role);
  // State to keep track if banner should be rendered
  const [globalNotification, setGlobalNotification] = useState({
    visible: false,
    text: undefined,
    variant: 'error',
  });

  const PERMISSIONS = _getPermissions(currentRole);

  const { location: { state } = {} } = props;
  const status = state?.userStatus;

  const notificationTimeout = () => setTimeout(() => {
    setGlobalNotification({
      visible: false,
    });
  }, 6000);

  useEffect(() => {
    if (status === 'PENDING' || status === 'EXPIRED') {
      dispatch({
        type: ActionTypes.teamMember.getInvite.START,
        token: memberId,
      });
    } else {
      dispatch({
        type: ActionTypes.teamMember.getUser.START,
        id: memberId,
      });
    }
  }, [status]);

  useEffect(() => {
    if (!teamMemberService?.fetching && teamMemberService?.success) {
      setCurrentRole(teamMemberService?.role);
    }
  }, [teamMemberService?.success, teamMemberService?.fetching]);

  useEffect(() => {
    reset({
      firstName: teamMemberService?.firstName,
      lastName: teamMemberService?.lastName,
      email: teamMemberService?.email,
      role: teamMemberService?.role,
    }, {
      dirtyFields: false,
    });

    return () => {
      if (notificationTimeout) clearTimeout(notificationTimeout());
    };
  }, [teamMemberService?.success, teamMemberService?.error]);

  const _getPatchOps = ({ data }) => {
    const ops = [];

    INPUT_FIELDS.forEach((field) => {
      if (!dirtyFields[field]) return;
      if (field === 'firstName' && data?.firstName) {
        ops.push(
          {
            op: 'replace',
            path: 'firstName',
            value: data?.firstName?.trim(),
          },
        );
      } else if (field === 'lastName' && data?.lastName) {
        ops.push(
          {
            op: 'replace',
            path: 'lastName',
            value: data?.lastName?.trim(),
          },
        );
      } else if (field === 'role' && data?.role) {
        ops.push({
          op: 'replace',
          path: status === 'PENDING' || status === 'EXPIRED' ? 'type' : 'role',
          value: data?.role,
        });
      }
    });
    return ops;
  };

  const onSubmit = async (data: {firstName: string, lastName: string, role: 'manager' | 'owner' | 'admin'}) => {
    const patchOps = _getPatchOps({ data });
    if (patchOps && patchOps.length < 1) return null;
    if (status === 'PENDING' || status === 'EXPIRED') {
      dispatch({
        type: ActionTypes.teamMember.patchInvite.START,
        body: patchOps,
        token: memberId,
      });
    } else {
      dispatch({
        type: ActionTypes.teamMember.patchUser.START,
        body: patchOps,
        id: memberId,
      });
    }
    if (teamMemberService?.success) {
      setGlobalNotification({
        visible: true,
        text: "Your team member's information has been updated.",
        variant: 'success',
      });
      notificationTimeout();
    }
    if (teamMemberService?.error) {
      setGlobalNotification({
        visible: true,
        text: "Your team member's information failed to update.",
        variant: 'error',
      });
      notificationTimeout();
    }
  };

  const canEditRole = teamMemberService?.canEditRole ?? true;

  return (
    <>
      <Helmet>
        <title>EditTeam | ZIYNX</title>
      </Helmet>
      {globalNotification?.visible && (
        <StyledBannerWrapper>
          <BannerNotification
            animated
            showCloseIcon
            variant={globalNotification.variant}
            timeout={5000}
          >
            {globalNotification?.text}
          </BannerNotification>
        </StyledBannerWrapper>
      )}
      <ContentContainer isVertical>
        <PageContentWrapper isMobile={isMobile} viewport={viewport}>
          <LinkWrapper viewport={viewport}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link variant="caret" onClick={() => history.goBack()} color={Colors.GrayDark}>
              Back to team
            </Link>
          </LinkWrapper>
          <Form
            formID="edit-team-member"
            title="Edit team member information"
            width="100%"
          >
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur, value,
                }) => {
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };

                  const onInputFocus = (): any => {
                    if (errors?.firstName) clearErrors('firstName');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.firstName}
                      disabled={!teamMemberService?.canEdit || teamMemberService?.fetching || status === 'CONFIRMED'}
                      errorLabel={errors?.firstName?.message}
                      inputName="firstName"
                      required
                      label="First name"
                      onBlur={onBlur}
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                      value={value}
                    />
                  );
                }}
                name="firstName"
                defaultValue={teamMemberService?.firstName || ' '}
                rules={{
                  required: 'Please enter a first name',
                  pattern: {
                    value: nameInputsRegex,
                    message: 'Please enter a valid first name',
                  },
                }}
              />
            </FormRow>
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur, value,
                }) => {
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };
                  const onInputFocus = (): any => {
                    if (errors.lastName) clearErrors('lastName');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.lastName}
                      errorLabel={errors?.lastName?.message}
                      inputName="lastName"
                      disabled={!teamMemberService?.canEdit || teamMemberService?.fetching || status === 'CONFIRMED'}
                      required
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                      onBlur={onBlur}
                      label="Last name"
                      value={value}
                    />
                  );
                }}
                name="lastName"
                defaultValue={teamMemberService?.lastName || ' '}
                rules={{
                  required: 'Please enter a last name',
                  pattern: {
                    value: nameInputsRegex,
                    message: 'Please enter a valid last name',
                  },
                }}
              />
            </FormRow>
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur, value,
                }) => {
                  /**
               * Function to clear any errors of inputs
               * when on focus event, if any
               */
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };

                  const onInputFocus = (): any => {
                    if (errors.email) clearErrors('email');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.email}
                      errorLabel={errors?.email?.message}
                      inputName="email"
                      disabled
                      required
                      label="Email"
                      onBlur={onBlur}
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                      value={value}
                    />
                  );
                }}
                name="email"
                defaultValue={teamMemberService?.email || ' '}
                rules={{
                  required: 'Please enter an email',
                  pattern: {
                    value: emailRegex,
                    message: 'Please enter a valid email',
                  },
                }}
              />
            </FormRow>
            <FormRow>
              <Dropdown
                inputName="role"
                activeLabel="Role"
                disabled={!teamMemberService?.canEdit
                  || !canEditRole
                  || teamMemberService?.fetching}
                label="Role"
                required
                options={ROLES}
                defaultValue={currentRole || ' '}
                onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                  (event?.target: HTMLInputElement);
                  setCurrentRole(event);
                  control.setValue('role', event, { shouldDirty: true });
                }}
                {...register({ name: 'role', type: 'custom' })}
              />
            </FormRow>
          </Form>
          <ListWrapper viewport={viewport}>
            <List variant="icon" content={PERMISSIONS} />
          </ListWrapper>
          <Button
            variant="primary"
            width={isMobile ? '100%' : '264px'}
            disabled={Object.keys(dirtyFields).length === 0 || Object.keys(errors).length > 0}
            isLoading={teamMemberService?.fetching}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </PageContentWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(EditTeamMemberScreen): React$ComponentType<ManagedProps<Props>>);
